import React from 'react';
import { FileCheck2, FilePenLine, MessageSquareText } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { ExportFileOptions } from '../../../services/QAgent';
import { useI18n } from '../../../i18n';

interface ExportDocumentMenuProps {
  fileName: string;
  onExport: (options: ExportFileOptions) => void;
  /** The button that opens the menu */
  children: React.ReactNode;
}

const isWordFile = (fileName: string) => /\.docx$/i.test(fileName);

/**
 * How to download a document with its accepted suggestions: a Word redline
 * (tracked changes) to send, the same with Queri's reasoning as comments for
 * internal use, or a clean copy (the changes applied, saved as a new version).
 */
export const ExportDocumentMenu: React.FC<ExportDocumentMenuProps> = ({
  fileName,
  onExport,
  children,
}) => {
  const { t } = useI18n();
  const em = t.qagentDocument.exportMenu;
  const canRedline = isWordFile(fileName);

  const item = (
    icon: React.ReactNode,
    label: string,
    hint: string,
    options: ExportFileOptions,
    disabled = false,
  ) => (
    <DropdownMenuItem
      disabled={disabled}
      onSelect={() => onExport(options)}
      className="items-start"
    >
      {icon}
      <div className="flex flex-col gap-0.5">
        <span className="text-sm">{label}</span>
        <span className="text-xs text-muted-foreground">
          {disabled ? em.redlineNeedsWord : hint}
        </span>
      </div>
    </DropdownMenuItem>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        {item(
          <FilePenLine className="mt-0.5" />,
          em.redline,
          em.redlineHint,
          { format: 'redline', comments: false },
          !canRedline,
        )}
        {item(
          <MessageSquareText className="mt-0.5" />,
          em.redlineWithComments,
          em.redlineWithCommentsHint,
          { format: 'redline', comments: true },
          !canRedline,
        )}
        {item(
          <FileCheck2 className="mt-0.5" />,
          em.clean,
          em.cleanHint,
          { format: 'clean' },
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/** The default for one-click exports (e.g. the /export command) */
export const defaultExportOptions = (fileName: string): ExportFileOptions =>
  isWordFile(fileName)
    ? { format: 'redline', comments: false }
    : { format: 'clean' };
